import { Constants } from "@core/Utils/Constants";
import { Language, Messages } from "./Language";
import { Client } from "@core/Client";
import { IMAXALockLevel, lock } from "@core/Logic/Links/IMAXA/IMAXAProtocol";
import Phaser from "phaser";

export class LockScreen extends Phaser.GameObjects.Container {

	private portraitMode: boolean = false;

	private currentlyActiveLock: lock | null = null;
	private appliedLocks: Array<lock> = new Array<lock>();

	private graphics: Phaser.GameObjects.Graphics;
	private reason: Phaser.GameObjects.Text;
	private message: Phaser.GameObjects.Text;

	constructor(scene: Phaser.Scene) {
		super(scene);

		//-- graphics ----------------------------------
		this.graphics = new Phaser.GameObjects.Graphics(this.scene, {});

		let blockInput = (pointer: Phaser.Input.Pointer, localX: number, localY: number, event: Phaser.Types.Input.EventData) => {
			if (!this.visible)
				return;

			event.stopPropagation();
		}
		let blockInputExitCancel = (pointer: Phaser.Input.Pointer, localX: number, localY: number, event: Phaser.Types.Input.EventData) => {
			if (!this.visible)
				return;

			if (Client.exiting && Client.logic.canRequestExit) {
				Client.logic.canRequestExit = false;
				Client.remote.SendRequest(Constants.CLIENT_REQUESTS.CANCEL_EXIT, null, null);
			}
			event.stopPropagation();
		}

		this.graphics.setInteractive(new Phaser.Geom.Rectangle(0, 0, scene.scale.width, scene.scale.height), Phaser.Geom.Rectangle.Contains);
		this.graphics.on("pointerover", blockInput);
		this.graphics.on("pointerup", blockInputExitCancel);
		this.graphics.on("pointermove", blockInput);
		this.graphics.on("pointerdown", blockInput);
		this.add(this.graphics);
		//----------------------------------------------


		//--- reason -----------------------------------
		this.reason = new Phaser.GameObjects.Text(this.scene, 0, 0, Language.Get(Messages.CLIENT_LOCKED), { fontSize: "50px", align: "center", strokeThickness: 13, stroke: "0x000000" }).setOrigin(0).setVisible(false);
		this.add(this.reason);
		//----------------------------------------------


		//---- message ---------------------------------
		this.message = new Phaser.GameObjects.Text(this.scene, 0, 0, Language.Get(Messages.CLIENT_LOCKED), { fontSize: "35px", align: "center", strokeThickness: 13, stroke: "0x000000" }).setOrigin(0).setVisible(false);
		this.add(this.message);
		//----------------------------------------------

		Client.game.events.on(Constants.EVENTS.ADD_LOCK, (l: lock) => { this.AddLock(l); });
		Client.game.events.on(Constants.EVENTS.REMOVE_LOCK, (l: lock | null) => { this.RemoveLock(l); });
	}

	BringToTop(): void {
		this.scene.children.bringToTop(this);
	}

	OnResize(width: number, height: number, portraitMode: boolean): void {
		this.width = width;
		this.height = height;
		this.x = 0;
		this.y = 0;
		if (portraitMode)
			this.y -= height;

		this.portraitMode = portraitMode;

		if (this.graphics) {
			this.graphics.clear();
			this.graphics.fillStyle(0x000000, Constants.PANEL.LOCK_SCREEN_OPACITY);

			if (portraitMode) {
				this.graphics.fillRect(0, 0, width, -height)
				this.graphics.input.hitArea.setSize(width * 1000, -(height * 1000))
			}
			else {
				this.graphics.fillRect(0, 0, width, height)
				this.graphics.input.hitArea.setSize(width * 1000, height * 1000)
			}
		}

		if (this.reason) {
			this.reason.setScale(1);
			let newFontSize = parseInt(this.reason.style.fontSize) * width / this.reason.displayWidth;
			this.reason.setFontSize(Math.min(newFontSize, 50))

			if (portraitMode)
				this.reason.setPosition(width / 2 - this.reason.displayWidth / 2, 0 - height / 2 - this.reason.displayHeight / 2)
			else
				this.reason.setPosition(width / 2 - this.reason.displayWidth / 2, (height / 2) - this.reason.displayHeight / 2)
		}

		if (this.message) {
			this.message.setScale(1);
			let newFontSize = parseInt(this.message.style.fontSize) * width / this.message.displayWidth;
			this.message.setFontSize(Math.min(newFontSize, 35))

			if (portraitMode)
				this.message.setPosition(width / 2 - this.message.displayWidth / 2, 0 - height / 2 - this.message.displayHeight / 2)
			else
				this.message.setPosition(width / 2 - this.message.displayWidth / 2, (height / 2) - this.message.displayHeight / 2)

			if (this.reason)
				this.message.y += this.reason.displayHeight * 1.5;
		}
	}

	AddLock(l: lock): void {
		console.log("add lock : " + JSON.stringify(l));
		//if we are not yet locked with this lock, apply it
		if (this.appliedLocks.filter(appliedL => appliedL.reason == l.reason).length == 0) {
			this.appliedLocks.push(l);
			this.LockModified(this.appliedLocks[0]);
		}
	}

	RemoveLock(l: lock | null): void {
		if (!l)//we got unlock client with no lock, so unlock the client
		{
			this.appliedLocks = new Array<lock>();
			this.LockModified(null);
		}

		//if we have this lock, remove it
		const i = this.appliedLocks.findIndex(appliedL => appliedL.reason == l.reason);
		if (i > -1) {
			this.appliedLocks.splice(i, 1);
		}

		if (this.appliedLocks.length == 0) {
			this.LockModified(null);
		}
		else
			this.LockModified(this.appliedLocks[0]);
	}

	private LockModified(l: lock | null): void {
		if (this.currentlyActiveLock == l && (l != null && this.visible))
			return;

		this.currentlyActiveLock = l;

		if (this.currentlyActiveLock == null)//no active lock = hide obviously
		{
			this.visible = false;
			Client.logic.locked = false;
		} else {
			Client.logic.locked = true;

			if (l.message.length > 0 && l.reason != IMAXALockLevel.UserClose) {
				this.message.text = l.message;
				this.message.visible = true;
			}
			else {
				this.message.visible = false;
			}

			if (l.reason.length > 0) {
				if (l.reason == IMAXALockLevel.UserClose)
					this.reason.text = Language.Get(Messages.CLOSING);
				else
					this.reason.text = l.reason;
				this.reason.visible = true;
			}
			else {
				this.reason.visible = false;
			}

			this.OnResize(this.width, this.height, this.portraitMode);
			this.visible = true;
			this.BringToTop();
		}
	}
}